// React
import { useCallback, useEffect, useState, MutableRefObject, useRef, useMemo } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import clsx from "clsx";
// Third party libraries
import { set } from "lodash-es";
// Helpers
import generateTailwindVariableClasses from "@/Common/helpers/TailwindCSS";
import { isAutoGeneratedFooter, addCustomPipeProcessor } from "@Layouts/components/Tables/DataSmartTable/helpers";
import { and, nullishResolver, or, renderIf, ternaryResolver } from "@/Common/helpers/sonar";
import { countVisibleRows } from "@/Common/helpers/gridTree";
// Components
import EmptyBody from "@Layouts/components/Basics/EmptyBody";
// MUI
import {
	DataGridPremium,
	GRID_AGGREGATION_FUNCTIONS,
	GridApiPro,
	GridCallbackDetails,
	GridEventListener,
	GridFilterModel,
	GridRowId,
	GridRowParams,
	GridState,
	MuiEvent,
	useGridApiRef,
} from "@mui/x-data-grid-premium";
import { Paper, Button, Box } from "@mui/material";
// Images
import emptyTable from "@/assets/images/commons/empty_table.png";
// Types & Constants
import {
	COLUMN_MIN_WIDTH_FALLBACK,
	COLUMN_WIDTH_FALLBACK,
	DEFAULT_HEADER_HEIGHT,
	DEFAULT_ROW_HEIGHT,
	MUI_SCROLLBAR_SIZE,
} from "@/Washouts/constants";
import { DataSmartTableProps } from "@Layouts/components/types";
import { COLUMN_RESIZE_FACTOR, FOOTER_PARAM_ID } from "@/Constants/tables";
// styles
import "../DataSmartTable/styles.css";
import { GridStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
// Hooks
import { useFilterStateStore } from "../../../../Common/hooks/TableFilterStore/useFilterStateStore";

const EXPANDED_ROW_CLASS = "expand-table-row";
const MIN_ROWS_IF_EMPTY = 2;
export const DEFAULT_ROW_EXPANSION_PANEL_HEIGHT = 130;
const EXPANDER_COLUMN_WIDTH = 20;

function overrideColumnDefinitions(columns: DataSmartTableProps["columns"], allowOverride: boolean) {
	let hasResizableColumns = !allowOverride;
	const vScrollCorrectionFactor = Math.floor(MUI_SCROLLBAR_SIZE / (columns.length ?? 1)) + 2;
	columns.forEach((column) => {
		if (and(column.resizable === undefined, allowOverride)) {
			column.resizable = and(Boolean(column.maxWidth), Boolean(column.minWidth)) && column.maxWidth !== column.minWidth;
		}
		if (column.resizable) {
			hasResizableColumns = true;
			if (allowOverride) {
				column.maxWidth = column.maxWidth ?? nullishResolver(column.width, COLUMN_WIDTH_FALLBACK) * COLUMN_RESIZE_FACTOR;
				column.minWidth = column.minWidth ?? nullishResolver(column.width, COLUMN_MIN_WIDTH_FALLBACK);
			}
		}
		if (column.width && allowOverride) {
			column.width -= vScrollCorrectionFactor;
			column.minWidth = column.minWidth ?? nullishResolver(column.width, COLUMN_MIN_WIDTH_FALLBACK);
		}
	});
	return hasResizableColumns;
}

function fillWithEmptyRows(rows: DataSmartTableProps["rows"] = [], maxRows = 0) {
	if (rows.length === 0 || rows.length >= maxRows) {
		return rows;
	}
	const filledRows = [...rows];
	for (let i = filledRows.length; i < maxRows - 1; ++i) {
		filledRows.push({ _id: `self-generated-empty-row-${i}`, emptyRow: true });
	}
	return filledRows;
}

function computeHeightByRowCount({ maxRows, ...props }: Partial<DataSmartTableProps>, rowCount = 0) {
	if (props.hasFlexibleRowHeight) {
		return "100%";
	}
	const scrollCorrection = ternaryResolver<string>(
		!props.disableHScrollCorrection,
		"var(--DataGrid-hasScrollX)* var(--DataGrid-scrollbarSize)",
		"0px"
	);
	const borderHeaderCorrection = props.disableHeaderBottomSeparator ? "-1px" : "0px";

	const hasAggregationFooter = Boolean(props.apiRef?.current?.state.rows?.tree[FOOTER_PARAM_ID]);
	const aggregationFooterCorrection = hasAggregationFooter ? 1 : 0;
	let factor = rowCount + aggregationFooterCorrection + (rowCount > 0 ? 0 : MIN_ROWS_IF_EMPTY);
	if (maxRows && (rowCount >= maxRows || props.enableRowsFiller)) {
		factor = maxRows;
	}
	return `calc(var(--DataGrid-headersTotalHeight) + ${factor} * var(--height) + ${scrollCorrection} + ${borderHeaderCorrection})`;
}

function computeHeightByState(props: Partial<DataSmartTableProps>, state: GridState) {
	if (props.hasFlexibleRowHeight) {
		return "100%";
	}
	const scrollCorrection = ternaryResolver<string>(
		!props.disableHScrollCorrection,
		"var(--DataGrid-hasScrollX)* var(--DataGrid-scrollbarSize)",
		"0px"
	);
	const borderHeaderCorrection = props.disableHeaderBottomSeparator ? "-1px" : "0px";

	const hasAggregationFooter = Boolean(state.rows?.tree[FOOTER_PARAM_ID]);
	const aggregationFooterCorrection = hasAggregationFooter ? 1 : 0;
	const visibleRowsHeight = state.rowsMeta.currentPageTotalHeight;
	const rowsCount = state.rows.totalRowCount;

	const maxHeight = props.maxRows
		? (props.maxRows - aggregationFooterCorrection) * nullishResolver(props.rowHeight, DEFAULT_ROW_HEIGHT)
		: visibleRowsHeight;

	let rowsTotalHeight = `${maxHeight}px`;
	if (or(and(rowsCount === 0, !props.enableRowsFiller), and(rowsCount > 0, visibleRowsHeight === 0))) {
		rowsTotalHeight = `calc(${MIN_ROWS_IF_EMPTY} * var(--height))`;
	} else if (rowsCount > 0 && visibleRowsHeight < maxHeight && !(props.treeData && props.enableRowsFiller)) {
		rowsTotalHeight = `${visibleRowsHeight}px`;
	}

	const corrections = `${aggregationFooterCorrection} * var(--height) + ${scrollCorrection} + ${borderHeaderCorrection}`;
	return `calc(var(--DataGrid-headersTotalHeight) + ${rowsTotalHeight} + ${corrections})`;
}

const defaultProps: DataSmartTableProps = {
	allowColDefOverride: true,
	enableRowsFiller: false,
	autoHeight: true,
	rowHeight: DEFAULT_ROW_HEIGHT,
	headerHeight: DEFAULT_HEADER_HEIGHT,
	disableHeaderBottomSeparator: false,
	disableHScrollCorrection: false,
	enableCustomScrollbars: false,
	enableSquaredScrollbars: false,
	disableStyledColumnHeaderBadge: false,
	disableHeightRounding: false,
	rows: [],
	columns: [], // definitions: https://mui.com/x/api/data-grid/grid-col-def/,
	columnGroupingModel: [], // definitions: https://mui.com/x/react-data-grid/column-groups/,
	hideFooter: true,
	emptyTableImageWidth: "340px",
	onRowClick: () => {},
	onFilterModelChange: () => {},
	customProps: {
		customAggregationsFn: {}, // definitions: https://mui.com/x/react-data-grid/aggregation/
		aggregationFooterClassName: "aggregation-footer",
		elevationContainer: 0,
		tableContainerStyle: {},
		tableContainerClassName: "",
		tableClassName: {},
		tableCustomClasses: {},
		footerTitle: "Total",
		expandableRows: false,
		hide: false,
		highlightRowOnClick: false,
		tableHidedProps: {
			currentMonth: false,
			onAddTheFirstOne: () => {},
			sectionFirstTitle: "",
		},
	},
};

const getHierarchyKey = (hierarchyKey?: string) => {
	if (!hierarchyKey) {
		return "hierarchy";
	}
	return hierarchyKey;
};

export default function DataSmartTable(paramProps: Readonly<DataSmartTableProps>) {
	const { customProps, allowColDefOverride = true, ...props } = { ...defaultProps, ...paramProps };
	const [treeMode, setTreeMode] = useState(Boolean(props.treeData));
	const [visibleRowsHeight, setVisibleRowsHeight] = useState<number>(-1);
	const [rowsCount, setRowsCount] = useState<number>(-1);

	const hasResizableColumns = overrideColumnDefinitions(props.columns, allowColDefOverride);
	const columnHeaderHeight = nullishResolver(props.headerHeight, DEFAULT_HEADER_HEIGHT);
	const rowHeight = nullishResolver(props.rowHeight, DEFAULT_ROW_HEIGHT);

	// Legacy expanded ids (for old expandableRows behavior and search toolbar integration)
	const [nodeExpandedIds, setNodeExpandedIds] = useState<GridRowId[]>([]);
	// New feature: controlled/uncontrolled expanded row ids
	const isExpansionControlled = Array.isArray(customProps.expandedRowIds);
	const [internalExpandedIds, setInternalExpandedIds] = useState<GridRowId[]>([]);
	const effectiveExpandedIds = (isExpansionControlled ? customProps.expandedRowIds : internalExpandedIds) || [];
	const initialHeight = rowHeight;
	const [maxTableHeight, setMaxTableHeight] = useState("100%");
	const hierarchy = getHierarchyKey(customProps?.hierarchyKey);

	const onRowClickHandler: GridEventListener<"rowClick"> = (params, event, details) => {
		const mode = props.apiRef?.current?.getRowMode(params.id);
		const rowNode = props.apiRef?.current?.getRowElement(params.id);
		if (rowNode) {
			if (customProps?.highlightRowOnClick && mode !== "edit") {
				rowNode.classList.toggle("highlight-row");
			}

			// Legacy expandableRows (deprecated internal) - maintain compatibility if someone manually toggles class elsewhere
			if (rowNode.classList.contains(EXPANDED_ROW_CLASS)) {
				setNodeExpandedIds([...new Set([...nullishResolver(nodeExpandedIds, []), params.id])]);
			} else if (nodeExpandedIds?.length) {
				const rowIdIndex = nodeExpandedIds?.indexOf(params.id);
				if (rowIdIndex !== -1) {
					setNodeExpandedIds(nodeExpandedIds.filter((_, i) => i !== rowIdIndex));
				}
			}
		}

		if (props?.onRowClick) {
			props.onRowClick(params, event, details);
		}
	};

	// New expansion toggle (chevron) independent from row click to avoid breaking existing handlers
	const toggleRowExpansion = useCallback(
		(id: GridRowId) => {
			const current = new Set(effectiveExpandedIds);
			if (current.has(id)) {
				current.delete(id);
			} else {
				current.add(id);
			}
			const updated = Array.from(current);
			if (!isExpansionControlled) {
				setInternalExpandedIds(updated);
			}
			customProps.onExpandedRowIdsChange?.(updated);
		},
		[effectiveExpandedIds, isExpansionControlled, customProps.onExpandedRowIdsChange]
	);

	const handleRowEditStop = (_: GridRowParams, event: MuiEvent) => {
		event.defaultMuiPrevented = true;
	};

	const getLastRowNode = useCallback((apiRef: MutableRefObject<GridApiPro> | null | undefined) => {
		const currentRowsCount = apiRef?.current?.getRowsCount();
		if (!currentRowsCount) {
			return null;
		}
		for (let i = currentRowsCount - 1; i >= 0; i--) {
			const rowNodeId = apiRef?.current?.getRowIdFromRowIndex(i);
			if (rowNodeId) {
				const rowNode = apiRef?.current?.getRowElement(rowNodeId);
				if (rowNode) {
					return rowNode;
				}
			}
		}
		return null;
	}, []);

	const clearProcessorRef = useRef<() => void>();

	const selfApiRef = useGridApiRef();
	const usedApiRef = nullishResolver(props.apiRef, selfApiRef);

	const onStateChange: GridEventListener<"stateChange"> = useCallback(
		(state: GridStatePremium, ...params) => {
			const currentVisibleRowsHeight = state.rowsMeta.currentPageTotalHeight;
			const currentRowsCount = state.rows.totalRowCount;
			if (currentVisibleRowsHeight !== visibleRowsHeight || currentRowsCount !== rowsCount) {
				setVisibleRowsHeight(currentVisibleRowsHeight);
				setRowsCount(currentRowsCount);
				setMaxTableHeight(computeHeightByState(props, state));
			}
			props.onStateChange?.(state, ...params);
		},
		[usedApiRef, props.onStateChange]
	);

	const onRowCountChange = useCallback(
		(count: number) => {
			setMaxTableHeight(computeHeightByRowCount(props, count));
		},
		[usedApiRef, props.onRowCountChange]
	);

	const resetCustomPipeProcessor = useCallback(() => {
		addCustomPipeProcessor(usedApiRef, clearProcessorRef);
		return usedApiRef.current?.subscribeEvent?.("rowExpansionChange", () => {
			usedApiRef.current.setRowCount(countVisibleRows(usedApiRef.current));
		});
	}, [usedApiRef, clearProcessorRef]);

	useEffect(() => {
		setTreeMode(Boolean(props.treeData));
		return resetCustomPipeProcessor();
		// If any prop change, force re-apply the custom processor
	}, [props, resetCustomPipeProcessor]);

	const { filterModel, updateFilterStore } = useFilterStateStore({
		initialFilterModel: props.initialState?.filter?.filterModel,
		key: props.filterStoreConfig?.key,
		persistAcrossLocations: props.filterStoreConfig?.persistAcrossLocations,
		persistAcrossRouteChange: props.filterStoreConfig?.persistAcrossRouteChange,
	});

	let initialState = props.initialState ?? {};

	if (and(!!props.filterStoreConfig, !!filterModel)) {
		initialState = {
			...initialState,
			filter: {
				filterModel: filterModel ?? initialState.filter?.filterModel,
			},
		};
	}

	const onFilterModelChange = (params: GridFilterModel, details: GridCallbackDetails<"filter">) => {
		updateFilterStore(params);
		// resetRowHeights
		handleNavigation();

		if (props?.onFilterModelChange) {
			props.onFilterModelChange(params, details);
		}
	};

	const handleNavigation = useCallback(() => {
		if (!nodeExpandedIds?.length) {
			return;
		}

		let parentElement: HTMLElement | null | undefined;
		let extraHeight = 0;
		let nodeIdsResult: GridRowId[] = [];

		nodeExpandedIds?.forEach((nodeId) => {
			const rowNode = props.apiRef?.current?.getRowElement(nodeId);
			if (rowNode && !rowNode.classList.contains("MuiDataGrid-row--lastVisible")) {
				rowNode.classList.remove(EXPANDED_ROW_CLASS);
			} else if (rowNode) {
				nodeIdsResult = [nodeId];
				extraHeight = rowNode.offsetHeight - nullishResolver(props?.rowHeight, 0);
			}

			parentElement = nullishResolver(parentElement, rowNode?.parentElement);
			if (!parentElement) {
				const lastRowNode = getLastRowNode(props.apiRef);
				if (lastRowNode?.parentElement) {
					lastRowNode.parentElement.style.height = `${initialHeight}px`;
				}
			}
		});
		if (parentElement) {
			parentElement.style.height = `${nullishResolver(initialHeight, 0) + extraHeight}px`;
		}
		setNodeExpandedIds(nodeIdsResult);
	}, [nodeExpandedIds, initialHeight, props.apiRef, props?.rowHeight, getLastRowNode]);

	function getComponentsProps() {
		const componentsProps = props.componentsProps;
		if (componentsProps?.toolbar?.search) {
			set(props.componentsProps ?? {}, "toolbar.search.nodeExpandedIds", nodeExpandedIds);
			set(props.componentsProps ?? {}, "toolbar.search.setNodeExpandedIds", setNodeExpandedIds);
		}
		return componentsProps;
	}

	// Inject expansion column + modify rows with expansion content if enabled
	const processedColumns = useMemo(() => {
		const mutable = Array.from(props.columns) as any[];
		let baseCols: any[] = mutable;
		if (customProps.disableFooterPrimaryBg) {
			baseCols = baseCols.map((c: any) => ({ ...c, _disableFooterPrimaryBg: true }));
		}
		if (!customProps.enableRowExpansion) return baseCols;
		const alreadyHas = props.columns.some((c: any) => c.field === "__expander");
		if (alreadyHas) return baseCols;
		const expanderWidth = customProps.expansionColumnOptions?.width ?? EXPANDER_COLUMN_WIDTH;
		const expansionCol = {
			field: "__expander",
			headerName: customProps.expansionColumnOptions?.headerName ?? "",
			width: expanderWidth,
			minWidth: customProps.expansionColumnOptions?.minWidth ?? expanderWidth,
			maxWidth: customProps.expansionColumnOptions?.maxWidth ?? expanderWidth,
			pinnable: false,
			sortable: false,
			filterable: false,
			resizable: false,
			disableColumnMenu: true,
			renderCell: (params: any) => {
				if (isAutoGeneratedFooter(params.id) || params.row.emptyRow) {
					return null;
				}
				const expanded = effectiveExpandedIds.includes(params.id);
				return (
					<button
						type="button"
						aria-label={expanded ? "Collapse row" : "Expand row"}
						className={clsx(
							"flex items-center justify-center w-full h-full text-[16px] transition-transform focus:outline-none text-default"
						)}
						onClick={(e) => {
							e.stopPropagation();
							toggleRowExpansion(params.id);
						}}
					>
						{expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
					</button>
				);
			},
			headerAlign: customProps.expansionColumnOptions?.headerAlign ?? "center",
			align: customProps.expansionColumnOptions?.align ?? "center",
		};
		return [expansionCol, ...props.columns];
	}, [customProps.enableRowExpansion, props.columns, effectiveExpandedIds, toggleRowExpansion, customProps.expansionColumnOptions]);

	const usingDetailPanel = Boolean(customProps.enableRowExpansion && customProps.getRowExpansionContent);

	const mergedColumnVisibilityModel = useMemo(() => {
		return {
			...(props.columnVisibilityModel || {}),
			detail_panel_toggle: false,
			__detail_panel_toggle__: false,
		} as Record<string, boolean>;
	}, [props.columnVisibilityModel]);

	const computedInitialState = useMemo(() => {
		const existingVisibility = initialState?.columns?.columnVisibilityModel || {};
		return {
			...initialState,
			columns: {
				...(initialState?.columns || {}),
				columnVisibilityModel: {
					...existingVisibility,
					detail_panel_toggle: false,
					__detail_panel_toggle__: false,
				},
			},
		};
	}, [initialState]);

	const userGetRowClassName = props.getRowClassName;
	const composedGetRowClassName = useCallback(
		(params: any) => {
			const base = userGetRowClassName?.(params);
			return clsx(base, { "ds-row-expanded": effectiveExpandedIds.includes(params?.id) });
		},
		[userGetRowClassName, effectiveExpandedIds]
	);

	if (customProps?.hide) {
		return (
			<EmptyBody
				image={emptyTable}
				imageStyles={{ width: nullishResolver(props.emptyTableImageWidth, "340px") }}
				title={
					<Box display="flex" alignItems="center">
						{nullishResolver(customProps?.tableHidedProps?.sectionFirstTitle, "")}
						{renderIf(
							!!customProps?.tableHidedProps?.currentMonth,
							<Button
								onClick={nullishResolver(customProps?.tableHidedProps?.onAddTheFirstOne, () => {})}
								disableElevation
								disableFocusRipple
								disableRipple
								className="!text-primary !font-montserrat !normal-case !underline !text-[16px] font-medium bg-transparent hover:!underline hover:!bg-transparent"
							>
								Add the first one!
							</Button>
						)}
					</Box>
				}
				sx={{
					minHeight: "390px",
					boxShadow: "none !important",
				}}
				bodyHeight={nullishResolver(props.emptyTableImageHeight, "380px")}
			/>
		);
	}

	return (
		<Paper
			elevation={customProps.elevationContainer ?? 0}
			sx={{
				...generateTailwindVariableClasses("--scrollbar-gap", "6px"),
				...customProps.tableContainerStyle,
			}}
			className={clsx(
				{
					"!mb-[calc(2*var(--scrollbar-gap))]": !props.disableHScrollCorrection,
				},
				customProps.tableContainerClassName
			)}
		>
			<DataGridPremium
				{...props}
				columns={processedColumns as any}
				columnVisibilityModel={mergedColumnVisibilityModel}
				rows={and(!!props.enableRowsFiller, !treeMode) ? fillWithEmptyRows(props.rows, props.maxRows) : props.rows}
				{...(usingDetailPanel
					? {
							detailPanelExpandedRowIds: effectiveExpandedIds as GridRowId[],
							getDetailPanelContent: (params: any) =>
								customProps.getRowExpansionContent?.({ id: params.id, row: params.row }) ?? null,
							getDetailPanelHeight: (params: any) => {
								const h = customProps.rowExpansionPanelHeight;
								if (typeof h === "function") {
									try {
										return h({ id: params.id, row: params.row }) ?? DEFAULT_ROW_EXPANSION_PANEL_HEIGHT;
									} catch {
										return DEFAULT_ROW_EXPANSION_PANEL_HEIGHT;
									}
								}
								return typeof h === "number" ? h : DEFAULT_ROW_EXPANSION_PANEL_HEIGHT;
							},
					  }
					: {})}
				sx={{
					...generateTailwindVariableClasses(
						"--max-table-height",
						ternaryResolver(!props.disableHeightRounding, `round(up, ${maxTableHeight}, 3px)`, maxTableHeight)
					),
					...props.sx,
				}}
				initialState={computedInitialState}
				rowHeight={rowHeight}
				autoHeight={props.maxRows ? false : props.autoHeight}
				apiRef={usedApiRef}
				columnHeaderHeight={columnHeaderHeight}
				disableColumnResize={props.disableColumnResize ?? !hasResizableColumns}
				// componentsProps was deprecated in favor of slotProps
				// https://mui.com/x/migration/migration-data-grid-v6/#removed-props
				// https://mui.com/material-ui/migration/migrating-from-deprecated-apis/#componentsprops
				// componentsProps={getComponentsProps()}
				slotProps={{
					...props.slotProps,
					...getComponentsProps(),
					basePopper: {
						// These style are present on the the displayed list when open a filter and click on columns to select one of these
						className: clsx({
							"[&_+.MuiPopover-root.MuiMenu-root.MuiModal-root>.MuiPaper-root>.MuiList-root>li]:!font-sans": true,
							"[&_+.MuiPopover-root.MuiMenu-root.MuiModal-root>.MuiPaper-root>.MuiList-root>li]:!text-[14.5px]": true,
							"[&_+.MuiPopover-root.MuiMenu-root.MuiModal-root>.MuiPaper-root>.MuiList-root>li]:!text-default": true,
							"[&_+.MuiPopper-root.MuiAutocomplete-popper>*]:!font-sans": true,
							"[&_+.MuiPopper-root.MuiAutocomplete-popper>*]:!text-[14.5px": true,
							"[&_+.MuiPopper-root.MuiAutocomplete-popper>*]:!text-default": true,
						}),
						sx: {
							zIndex: 990,
						},
						...props.slotProps?.basePopper,
					},
				}}
				getRowId={(row) => row?._id ?? row?.id}
				className={clsx({
					"data-smart-table": true,
					// Dynamic height
					"[&_.MuiDataGrid-main]:h-[--max-table-height]": true,
					// Disable borders
					"!border-none [&_.MuiDataGrid-cell]:!border-none [&_.MuiDataGrid-cellEmpty]:!border-none": true,
					// Force hide MUI internal detail panel toggle column (legacy or new expansion) always
					"[&_div[data-field='detail_panel_toggle']]:hidden [&_div[data-field='__detail_panel_toggle__']]:hidden": true,
					// Column header badge
					"[&_.MuiDataGrid-columnHeaderTitleContainer_.MuiBadge-badge]:text-white": !props.disableStyledColumnHeaderBadge,
					"[&_.MuiDataGrid-columnHeaderTitleContainer_.MuiBadge-badge]:bg-[#e41f75]": !props.disableStyledColumnHeaderBadge,
					"[&_.MuiDataGrid-columnHeaderTitleContainer_.MuiBadge-badge]:shadow-[0_0_0_2px_white]":
						!props.disableStyledColumnHeaderBadge,
					"[&_.MuiDataGrid-columnHeaderTitleContainer_.MuiBadge-badge]:scale-[0.72]": !props.disableStyledColumnHeaderBadge,
					// Misc styles overrides
					"[&_.MuiDataGrid-topContainer.MuiDataGrid-container--top:after]:hidden": props.disableHeaderBottomSeparator,
					"[&_.MuiDataGrid-row:hover:not(.MuiDataGrid-row--editing)]:!bg-[#E8E8E8]": true,
					"[&_.MuiDataGrid-columnSeparator:not(.MuiDataGrid-columnSeparator--resizable)]:!hidden": true,
					"[&_.MuiDataGrid-columnSeparator--resizable]:!inline-block": true,
					"[&_.MuiDataGrid-columnSeparator--resizable:hover]:!text-[color:--text-color]": true,
					"[&_.MuiDataGrid-columnSeparator--resizing]:!text-[color:--text-color]": true,
					"[&_.MuiDataGrid-bottomContainer_.MuiDataGrid-cellEmpty]:!bg-[color:--primary-color]": true,
					"!z-[1]": true,
					"[&_.tree-grid-child-row:has(+:not(.tree-grid-child-row))]:shadow-[inset_0_-1px_0_0_#CCC]": Boolean(props.treeData),
					"[&_.MuiDataGrid-filler--pinnedLeft]:!border-none": true,
					// Scrollbars
					"[&_.MuiDataGrid-main]:!mb-[calc(-1*var(--DataGrid-hasScrollX)*var(--DataGrid-scrollbarSize))]":
						!props.disableHScrollCorrection,
					"[&_.MuiDataGrid-main]:overflow-visible": props.enableCustomScrollbars,
					"[&_div.MuiDataGrid-filler>div]:!border-t-transparent": props.enableCustomScrollbars,
					"[&_.MuiDataGrid-scrollbarFiller.MuiDataGrid-scrollbarFiller--borderTop]:!border-none": props.enableCustomScrollbars,
					"[&_.MuiDataGrid-scrollbar--vertical]:absolute": props.enableCustomScrollbars,
					"[&_.MuiDataGrid-scrollbar--vertical]:-right-[calc(var(--DataGrid-scrollbarSize)+var(--scrollbar-gap))]":
						props.enableCustomScrollbars,
					"[&_.MuiDataGrid-scrollbar--horizontal]:absolute": props.enableCustomScrollbars,
					"[&_.MuiDataGrid-scrollbar--horizontal]:-bottom-[calc(var(--DataGrid-scrollbarSize)-(var(--scrollbar-gap)/2))]":
						props.enableCustomScrollbars,
					"[&_.MuiDataGrid-scrollbar--horizontal]:h-[calc(var(--DataGrid-scrollbarSize)+var(--scrollbar-gap))]":
						props.enableCustomScrollbars,
					"[&_.MuiDataGrid-scrollbar--horizontal]:shadow-[inset_0_0_0_calc(5*var(--DataGrid-scrollbarSize))_white]":
						props.enableCustomScrollbars,
					"[&_.MuiDataGrid-scrollbar::-webkit-scrollbar-thumb]:rounded-none": props.enableSquaredScrollbars,
					"[&_.MuiDataGrid-scrollbar::-webkit-scrollbar-track]:rounded-none": props.enableSquaredScrollbars,
					// Virtual cell borders
					"[&_.right-inset]:shadow-[inset_-1px_0_0_0_var(--disabled-and-stroke-color)]": true,
					...customProps.tableClassName,
				})}
				getRowClassName={(params) =>
					clsx(composedGetRowClassName(params), {
						"tree-grid-child-row": params?.row?.[hierarchy]?.length > 1,
						"!bg-primary [&_.MuiDataGrid-scrollbarFiller]:hover:!bg-primary [&_.MuiDataGrid-scrollbarFiller]:!border-t-primary":
							isAutoGeneratedFooter(params.id),
					})
				}
				classes={{
					"pinnedRows--bottom": customProps.aggregationFooterClassName ?? "aggregation-footer",
					...customProps.tableCustomClasses,
				}}
				aggregationFunctions={{
					...GRID_AGGREGATION_FUNCTIONS,
					...customProps.customAggregationsFn,
				}}
				onRowClick={onRowClickHandler}
				onRowEditStop={handleRowEditStop}
				onFilterModelChange={onFilterModelChange}
				onStateChange={treeMode ? onStateChange : props.onStateChange}
				onRowCountChange={!treeMode ? onRowCountChange : props.onRowCountChange}
			/>
		</Paper>
	);
}
